/*
 * Calendar days of the community, turned into instants. A filter "from 8 Oct to 14 Oct" means the
 * community's days (the Dashboard's charts count them that way), never the reader's browser zone.
 */

/** Milliseconds `zone` is ahead of UTC at the instant `utcMs`. */
const zoneOffsetMs = (utcMs: number, zone: string): number => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit'
  }).formatToParts(new Date(utcMs))
  const part = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value ?? 0)
  const wall = Date.UTC(part('year'), part('month') - 1, part('day'), part('hour'), part('minute'), part('second'))
  return wall - Math.floor(utcMs / 1000) * 1000
}

/** Midnight that starts the `YYYY-MM-DD` day in `zone`, in epoch ms; the browser's zone when `zone` is missing or unknown. */
const startOfDayMs = (key: string, zone?: string | null): number => {
  const [y, m, d] = key.split('-').map(Number)
  if (zone) {
    try {
      const wall = Date.UTC(y, m - 1, d)
      /* Second pass: the offset at the guessed instant, which differs from the first across a DST change. */
      const guess = wall - zoneOffsetMs(wall, zone)
      return wall - zoneOffsetMs(guess, zone)
    } catch {
      /* an unknown zone falls back to the browser's */
    }
  }
  return new Date(y, m - 1, d).getTime()
}

/** The start of the `YYYY-MM-DD` day in the community's zone, as ISO. */
export const startOfZonedDayIso = (key: string, zone?: string | null): string => new Date(startOfDayMs(key, zone)).toISOString()

/** The last second of the `YYYY-MM-DD` day in the community's zone, as ISO (inclusive end of a filter). */
export const endOfZonedDayIso = (key: string, zone?: string | null): string => {
  const [y, m, d] = key.split('-').map(Number)
  const next = new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10)
  return new Date(startOfDayMs(next, zone) - 1000).toISOString()
}

/** `YYYY-MM-DDTHH:mm` of an instant as the wall clock of `zone` (the browser's when missing), the shape a datetime-local input takes. */
export const toZonedWallClock = (iso: string | Date | null | undefined, zone?: string | null): string => {
  if (!iso) return ''
  const value = new Date(iso)
  if (Number.isNaN(value.getTime())) return ''
  let parts: Record<string, string>
  try {
    parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-US', {
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: zone || undefined
      }).formatToParts(value).map((p) => [p.type, p.value])
    )
  } catch {
    return ''
  }
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`
}

/**
 * A Date whose local fields read the wall clock of `zone` (the browser's when missing), so date-fns
 * `format` shows an event at its own zone's time whatever the reader's. For display only, never for
 * comparisons or arithmetic: the instant it holds is shifted. Invalid when `iso` is not a date.
 */
export const toZonedDate = (iso: string | Date | null | undefined, zone?: string | null): Date => {
  const wall = toZonedWallClock(iso, zone)
  return wall ? new Date(wall) : new Date(NaN)
}

/** The instant a `YYYY-MM-DDTHH:mm` wall clock of `zone` names, as ISO; null when the value is not a date. */
export const zonedWallClockToIso = (wall: string, zone?: string | null): string | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(wall)
  if (!match) return null
  const [y, m, d, h, min] = match.slice(1).map(Number)
  const local = Date.UTC(y, m - 1, d, h, min)
  if (zone) {
    try {
      /* Second pass: the offset at the guessed instant, which differs from the first across a DST change. */
      const guess = local - zoneOffsetMs(local, zone)
      return new Date(local - zoneOffsetMs(guess, zone)).toISOString()
    } catch {
      /* an unknown zone falls back to the browser's */
    }
  }
  const browser = new Date(y, m - 1, d, h, min)
  return Number.isNaN(browser.getTime()) ? null : browser.toISOString()
}
